import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "targets-and-targetables"
    }}>{`Targets and Targetables`}</h1>
    <p>{`Targets and Targetables are features provided by PWA Studio's `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/general-concepts/extensibility/"
      }}>{`extensibility framework`}</a>{`.
They give you the ability to change feature behaviors, component logic, and even component source code without editing a local copy of PWA Studio code.`}</p>
    <h2 {...{
      "id": "intercept-file"
    }}>{`Intercept file`}</h2>
    <p>{`The intercept file is where you directly interact with Targets and Targetables to apply customizations.
It exports a default function that accepts a TargetProvider parameter.`}</p>
    <h3 {...{
      "id": "intercept-file-name-and-location"
    }}>{`Intercept file name and location`}</h3>
    <p>{`The file name and location of your intercept file is a custom value you specify in your project's `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file.`}</p>
    <p>{`To register the location of your intercept file, set the value for `}<inlineCode parentName="p">{`pwa-studio.targets.intercept`}</inlineCode>{`.
For example, the following entry registers `}<inlineCode parentName="p">{`src/targets/intercept.js`}</inlineCode>{` as this project's intercept file.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"pwa-studio": {
    "targets": {
      "intercept": "src/targets/intercept"
    }
}
`}</code></pre>
    <h3 {...{
      "id": "how-interception-works"
    }}>{`How interception works`}</h3>
    <p>{`Interception logic executes during the build process.
The `}<inlineCode parentName="p">{`@magento/pwa-buildpack`}</inlineCode>{` module creates a BuildBus process to run intercept files in the storefront project or its dependencies.`}</p>
    <p>{`The BuildBus process executes intercept files in `}<strong parentName="p">{`named direct dependencies`}</strong>{` in a project.
This means that modules listed under `}<inlineCode parentName="p">{`dependencies`}</inlineCode>{` and `}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{` in a project's `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file have the ability to intercept Targets and Targetables in the project.
The process does not execute intercept files in dependencies beyond those modules in the dependency tree.`}</p>
    <h3 {...{
      "id": "interception-order"
    }}>{`Interception order`}</h3>
    <p>{`The interception process executes files in dependency order.
This means that if your module declares another module that has an intercept file as a peer dependency, the other module's intercept file executes first.`}</p>
    <h2 {...{
      "id": "target"
    }}>{`Target`}</h2>
    <p>{`A `}<em parentName="p">{`Target`}</em>{` is a formal extension point exposed by a package.
Buildpack, Peregrine, and Venia UI expose their own set of Targets that let developers change the behavior of the underlying code.
Targets are a package's public API and can be intercepted in storefront or extension projects.`}</p>
    <h3 {...{
      "id": "intercepting-targets"
    }}>{`Intercepting Targets`}</h3>
    <p>{`Use the TargetProvider given to the intercept file's default function to access Targets from your project's dependencies.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`module.exports = (targets) => {
  // Set buildpack features
  const builtins = targets.of("@magento/pwa-buildpack");
  builtins.specialFeatures.tap((featuresByModule) => {
    featuresByModule["my-extension"] = {
      // Tells buildpack that this extension uses ES Modules
      esModules: true,
    };
  });
};
`}</code></pre>
    <h3 {...{
      "id": "pwa-studio-target-reference"
    }}>{`PWA Studio target reference`}</h3>
    <p>{`The following PWA Studio packages contain Targets that developers can intercept:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/commerce/pwa-studio/api/buildpack/targets/"
        }}>{`Buildpack`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/commerce/pwa-studio/api/peregrine/extension-points/targets/"
        }}>{`Peregrine`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/commerce/pwa-studio/api/venia/targets/"
        }}>{`Venia UI`}</a></li>
    </ul>
    <h2 {...{
      "id": "targetables"
    }}>{`Targetables`}</h2>
    <p><em parentName="p">{`Targetables`}</em>{` are objects that give you access to the source code for the files in your project or library.
They let you change the final application bundles by applying transformations to the source code during the build process.`}</p>
    <p>{`Targetables are useful in two scenarios:`}</p>
    <ul>
      <li parentName="ul">{`As a `}<strong parentName="li">{`storefront developer`}</strong>{`, you can use Targetables to transform the source file in any of your project's dependencies.`}</li>
      <li parentName="ul">{`As an `}<strong parentName="li">{`extension developer`}</strong>{`, you can use Targetables to add Targets to your own extension.`}</li>
    </ul>
    <h3 {...{
      "id": "access-the-targetableset-class"
    }}>{`Access the TargetableSet class`}</h3>
    <p>{`The most common pattern for working with Targetable objects is to connect an instance of the `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/api/buildpack/targetables/TargetableSet/"
      }}>{`TargetableSet`}</a>{` to the TargetProvider sent to the intercept file.
Then, you can use that instance to create Targetable objects.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// Access the TargetableSet class
const { Targetables } = require("@magento/pwa-buildpack");

module.exports = (targets) => {
  // Create a TargetableSet instance connected to this project's TargetProvider
  const targetables = Targetables.using(targets);

  // Use the TargetableSet to create Targetable objects
};
`}</code></pre>
    <h3 {...{
      "id": "create-targetable-objects"
    }}>{`Create Targetable objects`}</h3>
    <p>{`Use the TargetableSet instance to create a `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/api/buildpack/targetables/TargetableModule/"
      }}>{`TargetableModule`}</a>{` class object or one of its subclasses.
The TargetableModule class itself represents a plain module.
It contains functions that let it manipulate the source code directly.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const { Targetables } = require("@magento/pwa-buildpack");

module.exports = (targets) => {
  const targetables = Targetables.using(targets);

  // Create a TargetableModule instance that points to the main.js source
  const MainComponent = targetables.module(
    "@magento/venia-ui/lib/components/Main/main.js"
  );

  // Insert a console log message in the source
  MainComponent.insertAfterSource(
    "const Main = props => {\\n",
    '\\tconsole.log("Hello World");\\n'
  );
};
`}</code></pre>
    <p>{`Other Targetable classes, such as `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/api/buildpack/targetables/TargetableESModule/"
      }}>{`TargetableESModule`}</a>{`, are subclasses of TargetableModule.
They contain specialized functions that let it work with different modules or file types.`}</p>
    <h3 {...{
      "id": "chain-transformations"
    }}>{`Chain transformations`}</h3>
    <p>{`Some Targetable classes, such as TargetableModule and `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/api/buildpack/targetables/TargetableReactComponent/"
      }}>{`TargetableReactComponent`}</a>{`, support method chaining.
Certain methods in these classes are chainable, which let you call these methods one after another in your code.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const { Targetables } = require("@magento/pwa-buildpack");

module.exports = (targets) => {
  const targetables = Targetables.using(targets);

  const MainComponent = targetables.reactComponent(
    "@magento/venia-ui/lib/components/Main/main.js"
  );

  MainComponent.appendJSX(
    "div className={pageClass}",
    "<span>appendJSX succeeded!</span>"
  )
    .addJSXClassName("Header", '"myClass"')
    .insertBeforeJSX("<Header />", "<span>insertBeforeJSX succeeded!</span>");
};
`}</code></pre>
    <h3 {...{
      "id": "unbound-targetable-objects"
    }}>{`Unbound Targetable objects`}</h3>
    <p>{`A less common pattern for using Targetables is to access the Targetables classes directly and creating an object that is not connected to a project's TargetProvider.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const { Targetables } = require("@magento/pwa-buildpack");

module.exports = (targets) => {
  // Create an unbound Targetable ESModule object from a file
  const handlers = new Targetables.ESModule("src/lib/handlers.js");

  // Wrap an export in a decorator from another file
  handlers.wrapWithFile("handleLoad", "src/overrides/doSomethingOnLoad.js");

  // Send it all to the build
  targets.of("@magento/pwa-buildpack").transformModules.tap((addTransform) => {
    handlers.flush().forEach((request) => addTransform(request));
  });
};
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      